import React from 'react';
import { motion } from 'framer-motion';
import { Coins } from 'lucide-react';

interface GTDisplayProps {
  amount: number;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  animated?: boolean;
  className?: string;
}

export const GTDisplay: React.FC<GTDisplayProps> = ({
  amount,
  size = 'md',
  showIcon = true,
  animated = false,
  className = ''
}) => {
  const sizes = {
    sm: { text: 'text-sm', icon: 14, padding: 'px-2 py-1' },
    md: { text: 'text-base', icon: 18, padding: 'px-3 py-1.5' },
    lg: { text: 'text-lg', icon: 22, padding: 'px-4 py-2' }
  };

  const { text, icon, padding } = sizes[size];

  const formatAmount = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toLocaleString();
  };

  if (animated) {
    return (
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        className={`inline-flex items-center gap-1.5 ${padding} bg-primary/10 text-primary rounded-full font-semibold ${className}`}
      >
        {showIcon && (
          <Coins size={icon} className="text-primary" />
        )}
        <span className={text}>{formatAmount(amount)} GT</span>
      </motion.div>
    );
  }

  return (
    <div
      className={`inline-flex items-center gap-1.5 ${padding} bg-primary/10 text-primary rounded-full font-semibold ${className}`}
    >
      {showIcon && (
        <Coins size={icon} className="text-primary" />
      )}
      <span className={text}>{formatAmount(amount)} GT</span>
    </div>
  );
};

interface GTChangeProps {
  amount: number;
  size?: 'sm' | 'md' | 'lg';
}

export const GTChange: React.FC<GTChangeProps> = ({ amount, size = 'md' }) => {
  const isPositive = amount > 0;
  const sizes = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg'
  };

  return (
    <motion.span
      initial={{ y: -10, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className={`${sizes[size]} font-semibold ${isPositive ? 'text-success' : 'text-error'}`}
    >
      {isPositive ? '+' : ''}{amount.toLocaleString()} GT
    </motion.span>
  );
};

export default GTDisplay;
